import type { LayoutConstraint, LayoutItem, ResizeHandleAxis } from "react-grid-layout/core"
import type { ChartType } from "@/types/pdfBuilder"

// 허용 너비 (grid columns). 처음엔 25/33/50/100% 네 단계만 허용했지만 컴포넌트를 더 자유롭게 놓을 수 있게
// 2칸(1/6, 약 130px) 이상은 모두 허용한다 (2026-09-23 요청). 1칸(약 63px)은 어떤 컴포넌트도 읽을 수 없어 뺀다.
export const ALLOWED_WIDTHS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const
export type AllowedWidth = (typeof ALLOWED_WIDTHS)[number]

// 대표 너비 라벨 (나머지 칸 수는 라벨 없이 그대로 쓴다)
export const WIDTH_LABELS: Partial<Record<AllowedWidth, string>> = {
  3: "Quarter",
  4: "Third",
  6: "Half",
  12: "Full",
}

// 차트 타입별 높이 프리셋 (grid rows, ROW_HEIGHT=30px)
export const HEIGHT_PRESETS: Record<ChartType, { S: number; M: number; L: number }> = {
  activityInfo: { S: 2, M: 3, L: 4 },
  summary: { S: 2, M: 3, L: 4 },
  bar: { S: 3, M: 5, L: 8 },
  line: { S: 3, M: 5, L: 8 },
  combo: { S: 3, M: 5, L: 8 },
  activityGraph: { S: 3, M: 5, L: 8 },
  point: { S: 3, M: 5, L: 8 },
  sessionDetails: { S: 4, M: 6, L: 9 },
  scatter: { S: 6, M: 9, L: 12 },
  table: { S: 3, M: 5, L: 8 },
  map: { S: 4, M: 6, L: 8 },
  comparison: { S: 3, M: 5, L: 8 },
  spacer: { S: 1, M: 2, L: 4 },
}

// 가장 가까운 허용 너비로 스냅
export function snapWidth(w: number): number {
  let closest = ALLOWED_WIDTHS[0] as number
  let minDiff = Math.abs(w - closest)
  for (const aw of ALLOWED_WIDTHS) {
    const diff = Math.abs(w - aw)
    if (diff < minDiff) {
      minDiff = diff
      closest = aw
    }
  }

  return closest
}

// 가장 가까운 높이 프리셋으로 스냅
export function snapHeight(h: number, chartType: ChartType): number {
  const presets = HEIGHT_PRESETS[chartType]
  const values = [presets.S, presets.M, presets.L]
  let closest = values[0]
  let minDiff = Math.abs(h - closest)
  for (const v of values) {
    const diff = Math.abs(h - v)
    if (diff < minDiff) {
      minDiff = diff
      closest = v
    }
  }

  return closest
}

/** 격자 한 줄의 높이 (ROW_HEIGHT 30 + 세로 margin 10) — 캔버스(ReportCanvas)와 같은 값 */
const GRID_ROW_UNIT_PX = 40
/** 같은 줄 이웃의 아래끝에 맞춰 주는 허용 오차 (2026-10-02 요청: 20px) */
export const ROW_ALIGN_TOLERANCE_PX = 20
const ROW_ALIGN_TOLERANCE = ROW_ALIGN_TOLERANCE_PX / GRID_ROW_UNIT_PX

/**
 * 커스텀 LayoutConstraint: 아래쪽 손잡이로 높이를 바꿀 때, 같은 줄(세로로 겹치는) 이웃의 아래끝과
 * 20px 안으로 가까워지면 딱 맞춰 준다 (2026-10-02 요청). 위쪽 손잡이는 y 가 같이 움직여 기준이 달라지므로 두지 않는다.
 * 손을 따라오는 h 는 아직 정수로 떨어지기 전(소수)이라 줄 수가 아니라 px 로 재야 20px 이 뜻을 가진다.
 */
export function createRowAlignConstraint(getItems: () => LayoutItem[]): LayoutConstraint {
  return {
    name: "rowAlign",
    constrainSize(item: LayoutItem, w: number, h: number, handle: ResizeHandleAxis) {
      if (!handle.includes("s")) {
        return { w, h }
      }
      let snapped = h
      let bestDiff = ROW_ALIGN_TOLERANCE + 1
      for (const other of getItems()) {
        if (other.i === item.i) {
          continue
        }
        // 같은 줄 = 세로로 겹치는 것
        if (other.y >= item.y + h || other.y + other.h <= item.y) {
          continue
        }
        const target = other.y + other.h - item.y
        if (target < Math.max(1, item.minH ?? 1)) {
          continue
        }
        const diff = Math.abs(h - target)
        if (diff > 0 && diff <= ROW_ALIGN_TOLERANCE && diff < bestDiff) {
          bestDiff = diff
          snapped = target
        }
      }

      return { w, h: snapped }
    },
  }
}

// 커스텀 LayoutConstraint: 너비를 허용 값으로 스냅
export const widthSnapConstraint: LayoutConstraint = {
  name: "widthSnap",
  constrainSize(_item: LayoutItem, w: number, h: number, _handle: ResizeHandleAxis) {
    return { w: snapWidth(w), h }
  },
}
